import { ComputedPlan, ComputedStep, CustomStep, Plan, Step } from './types';

const MEDICINE_OFFSET_MINUTES = 60;

export function stepBaseMinutes(step: Step): number {
  if (step.type === 'custom') {
    return Math.max(0, step.durationMinutes || 0);
  }
  return Math.max(0, step.drivingMinutes || 0);
}

export function applyPadding(base: number, step: Step): number {
  if (step.type !== 'driving') return base;
  const value = step.paddingValue || 0;
  if (step.paddingUnit === 'percent') {
    return base * (1 + value / 100);
  }
  return base + value;
}

export function computePlan(plan: Plan): ComputedPlan {
  const appointmentAt = plan.appointmentTime ? new Date(plan.appointmentTime) : null;
  const isValidAppointment = appointmentAt !== null && !isNaN(appointmentAt.getTime());

  const arriveAt = isValidAppointment
    ? new Date(appointmentAt!.getTime() - (plan.arriveEarlyMinutes || 0) * 60_000)
    : null;

  let totalPrep = 0;
  for (const step of plan.steps) {
    totalPrep += applyPadding(stepBaseMinutes(step), step);
  }

  const leaveByAt = arriveAt
    ? new Date(arriveAt.getTime() - totalPrep * 60_000)
    : null;

  const computedSteps: ComputedStep[] = [];
  let cursor = leaveByAt ? leaveByAt.getTime() : 0;
  let earliestBreakfast: ComputedStep | null = null;
  for (const step of plan.steps) {
    const base = stepBaseMinutes(step);
    const effective = applyPadding(base, step);
    const startsAt = new Date(cursor);
    const endsAt = new Date(cursor + effective * 60_000);
    const cs: ComputedStep = { step, baseMinutes: base, effectiveMinutes: effective, startsAt, endsAt };
    computedSteps.push(cs);
    if (!earliestBreakfast && step.name.toLowerCase().includes('breakfast')) {
      earliestBreakfast = cs;
    }
    cursor = endsAt.getTime();
  }

  if (earliestBreakfast) {
    const startsAt = new Date(earliestBreakfast.startsAt.getTime() - MEDICINE_OFFSET_MINUTES * 60_000);
    const medicineStep: CustomStep = {
      id: `medicine-${earliestBreakfast.step.id}`,
      type: 'custom',
      name: 'Take medicine',
      durationMinutes: 0,
    };
    computedSteps.push({
      step: medicineStep,
      baseMinutes: 0,
      effectiveMinutes: 0,
      startsAt,
      endsAt: new Date(startsAt.getTime()),
      isMedicine: true,
    });
    computedSteps.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
  }

  return {
    steps: computedSteps,
    totalPrepMinutes: totalPrep,
    leaveByAt,
    arriveAt,
    appointmentAt,
  };
}

export function formatTime(d: Date | null): string {
  if (!d || isNaN(d.getTime())) return '--:--';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function formatMinutes(mins: number): string {
  const rounded = Math.round(mins);
  if (rounded < 60) return `${rounded} min`;
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}
